import { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { TEXT_LINK_FOCUS_RING } from '../../../lib/a11yFocus'

const CONTENT = {
	notFound: {
		title: 'Oh no, 404!',
		message: 'The page you are looking for does not exist.',
	},
	unexpected: {
		title: 'Something went wrong',
		message: 'This page failed to load. Please try again, or contact support if the problem continues.',
	},
}

type ErrorPageProps = {
	variant: keyof typeof CONTENT
}

// Canonical error page: rendered inside the app shell so it shares the top bar and logo with every other page.
export default function ErrorPage({ variant }: ErrorPageProps) {
	const { title, message } = CONTENT[variant]

	return (
		<div data-testid={`error-page-${variant}`} className='content w-full max-w-[32rem] px-4 sm:px-0 mx-auto mt-16 mb-8 text-center'>
			<h1 className='text-charcoal-100 text-2xl'>{title}</h1>
			<p className='mt-4 text-charcoal-100'>{message}</p>
			<div className='mt-6'>
				<Link className={`text-link ${TEXT_LINK_FOCUS_RING}`} to='/'>
					Go to Neoni
				</Link>
			</div>
		</div>
	)
}

// Error boundary fallback: resets once the user navigates elsewhere.
export function ErrorBoundaryFallback({ resetErrorBoundary }: { resetErrorBoundary: () => void }) {
	const location = useLocation()
	const [startLocation] = useState(location)

	useEffect(() => {
		if (location.pathname !== startLocation.pathname) {
			resetErrorBoundary()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location])

	return <ErrorPage variant='unexpected' />
}

export function logError(error: Error) {
	console.error(error)
}
